import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendDown,
  faArrowTrendUp,
  faCopy,
  faDumbbell,
  faInfoCircle,
  faPaste,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Input, Select, Tooltip } from "antd";
import DecimalInputNumber from "../../common/DecimalInputNumber";
import {
  PrecompiledSet,
  PrecompiledWeek,
  PrecompiledWeekProgressionRule,
  ProgressionMethodConfig,
  SogliaThreshold,
} from "../../types/Entities";
import { parseMaxReps } from "../../utils/progressionEngine";
import {
  applySiSConfig,
  applyStSConfig,
  computePrevSlotProjection,
  enumerateSourceCandidates,
  extractSiSConfig,
  extractStSConfig,
} from "../../utils/precompiledRuleApply";
import CollapsibleSection from "./CollapsibleSection";

// ─── FieldGroup ───────────────────────────────────────────────────────────────

function FieldGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="pp-field-group">
      <span className="pp-field-label">{label}</span>
      {children}
    </div>
  );
}

// ─── BoxClipboardToolbar ───────────────────────────────────────────────────────
// Per-box Copia/Incolla for the StS / SiS method panels. `onPaste` is omitted
// (button disabled) when the clipboard has no copied box config.

function BoxClipboardToolbar({
  onCopy,
  onPaste,
}: {
  onCopy: () => void;
  onPaste?: () => void;
}) {
  return (
    <div className="pp-box-clipboard">
      <button
        type="button"
        className="pp-box-clipboard-btn"
        onClick={onCopy}
        title="Copia questo box"
      >
        <FontAwesomeIcon icon={faCopy} /> Copia
      </button>
      <button
        type="button"
        className="pp-box-clipboard-btn"
        onClick={onPaste}
        disabled={!onPaste}
        title={onPaste ? "Incolla nel box" : "Nessun box copiato"}
      >
        <FontAwesomeIcon icon={faPaste} /> Incolla
      </button>
    </div>
  );
}

// ─── RepsValueInput ───────────────────────────────────────────────────────────
// FE-3 (#ae917a1e) — free-form reps cell that accepts a single integer ("8")
// or a range ("8-10" / "8–10"). Validates against the same regex the engine
// uses (FE-1 isRepsRange / getSogliaSchemeValue). Persists trimmed string when
// the value contains "-"/"–", number when it's a single integer.

const REPS_VALUE_PATTERN = /^\d+(\s*[-–]\s*\d+)?$/;

function RepsValueInput({
  value,
  onChange,
}: {
  value: number | string | null | undefined;
  onChange: (v: number | string | null) => void;
}) {
  const [draft, setDraft] = useState<string>(
    value == null ? "" : String(value),
  );
  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  const trimmed = draft.trim();
  const isEmpty = trimmed === "";
  const isValid = isEmpty || REPS_VALUE_PATTERN.test(trimmed);

  const commit = () => {
    if (isEmpty) {
      onChange(null);
      return;
    }
    if (!isValid) return;
    if (/[-–]/.test(trimmed)) {
      onChange(trimmed);
    } else {
      const n = parseInt(trimmed, 10);
      onChange(Number.isFinite(n) ? n : null);
    }
  };

  return (
    <Input
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      status={!isValid ? "error" : undefined}
      addonAfter="reps"
      style={{ width: 130 }}
      placeholder="reps o range"
    />
  );
}

// ─── ThresholdList ────────────────────────────────────────────────────────────

function ThresholdList({
  label,
  thresholds,
  onChange,
  incrementTarget,
  loadChangeType,
}: {
  label: string;
  thresholds: SogliaThreshold[];
  onChange: (v: SogliaThreshold[]) => void;
  incrementTarget: "reps" | "load" | "both";
  loadChangeType: "percentage" | "kg" | null;
}) {
  const add = () =>
    onChange([...thresholds, { threshold: 1, loadValue: null, repsValue: null }]);
  const remove = (i: number) => onChange(thresholds.filter((_, j) => j !== i));
  const update = (i: number, partial: Partial<SogliaThreshold>) => {
    const next = [...thresholds];
    next[i] = { ...next[i], ...partial };
    onChange(next);
  };

  const showLoad = incrementTarget === "load" || incrementTarget === "both";
  const showReps = incrementTarget === "reps" || incrementTarget === "both";

  return (
    <div className="pp-threshold-list">
      <div className="pp-threshold-list-label">
        <span>{label}</span>
        <button type="button" className="pp-threshold-add-btn" onClick={add}>
          <FontAwesomeIcon icon={faPlus} /> Soglia
        </button>
      </div>
      {thresholds.length === 0 && (
        <div className="pp-threshold-empty">Nessuna soglia configurata</div>
      )}
      {thresholds.map((t, i) => (
        <div key={i} className="pp-threshold-row">
          <span className="pp-threshold-label">Δ reps ≥</span>
          <DecimalInputNumber
            value={t.threshold}
            onChange={(v) => update(i, { threshold: v ?? 0 })}
            min={0}
            style={{ width: 72 }}
          />
          <span className="pp-threshold-arrow">→</span>
          {showLoad && (
            <DecimalInputNumber
              value={t.loadValue ?? undefined}
              onChange={(v) => update(i, { loadValue: v ?? null })}
              min={0}
              addonAfter={loadChangeType === "percentage" ? "%" : "kg"}
              style={{ width: 110 }}
              placeholder="valore"
            />
          )}
          {showReps && (
            <RepsValueInput
              value={t.repsValue}
              onChange={(v) => update(i, { repsValue: v })}
            />
          )}
          <button
            type="button"
            className="pp-threshold-remove"
            onClick={() => remove(i)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── ThresholdSection ─────────────────────────────────────────────────────────

function ThresholdSection({
  title,
  positiveThresholds,
  negativeThresholds,
  onChangePositive,
  onChangeNegative,
  incrementTarget,
  loadChangeType,
}: {
  title: string;
  positiveThresholds: SogliaThreshold[];
  negativeThresholds: SogliaThreshold[];
  onChangePositive: (v: SogliaThreshold[]) => void;
  onChangeNegative: (v: SogliaThreshold[]) => void;
  incrementTarget: "reps" | "load" | "both";
  loadChangeType: "percentage" | "kg" | null;
}) {
  return (
    <div className="pp-thresholds-section">
      <div className="pp-thresholds-section-title">{title}</div>
      <Row className="g-3">
        <Col xs={12} sm={6}>
          <div className="pp-threshold-polarity positive">
            <FontAwesomeIcon icon={faArrowTrendUp} className="me-1" />
            Soglie positive — reps oltre il massimo
          </div>
          <ThresholdList
            label=""
            thresholds={positiveThresholds}
            onChange={onChangePositive}
            incrementTarget={incrementTarget}
            loadChangeType={loadChangeType}
          />
        </Col>
        <Col xs={12} sm={6}>
          <div className="pp-threshold-polarity negative">
            <FontAwesomeIcon icon={faArrowTrendDown} className="me-1" />
            Soglie negative — reps sotto il minimo
          </div>
          <ThresholdList
            label=""
            thresholds={negativeThresholds}
            onChange={onChangeNegative}
            incrementTarget={incrementTarget}
            loadChangeType={loadChangeType}
          />
        </Col>
      </Row>
    </div>
  );
}

// ─── Week2 method field-groups ────────────────────────────────────────────────

function computeIncrementPreview(
  baseLoad: number,
  direction: "increment" | "decrement" | null | undefined,
  loadChangeType: "percentage" | "kg" | null | undefined,
  loadChangeValue: number | null | undefined,
): number | null {
  if (!direction || !loadChangeValue) return null;
  const sign = direction === "decrement" ? -1 : 1;
  const unit = loadChangeType ?? "kg";
  if (unit === "kg") return Math.round((baseLoad + sign * loadChangeValue) * 100) / 100;
  return Math.round(baseLoad * (1 + sign * loadChangeValue / 100) * 100) / 100;
}

function FixedIncrementFields({
  direction, onDirectionChange,
  target, onTargetChange,
  repsChange, onRepsChange,
  loadChangeType, onLoadChangeTypeChange,
  loadChangeValue, onLoadChangeValueChange,
  baseLoad,
}: {
  direction: "increment" | "decrement" | null | undefined;
  onDirectionChange: (v: "increment" | "decrement") => void;
  target: "load" | "reps" | "both" | null | undefined;
  onTargetChange: (v: "load" | "reps" | "both") => void;
  repsChange: number | string | null | undefined;
  onRepsChange: (v: number | string | null) => void;
  loadChangeType: "percentage" | "kg" | null | undefined;
  onLoadChangeTypeChange: (v: "percentage" | "kg") => void;
  loadChangeValue: number | null | undefined;
  onLoadChangeValueChange: (v: number | null) => void;
  baseLoad?: number | null;
}) {
  const effectiveUnit = loadChangeType ?? "kg";
  const previewLoad =
    baseLoad != null && (target === "load" || target === "both")
      ? computeIncrementPreview(baseLoad, direction, loadChangeType, loadChangeValue)
      : null;

  return (
    <Row className="g-2">
      {/* FE-F (#a2bde480) — direction is meaningless for an absolute reps
          target. Hide entirely when target==="reps". Keep visible for "load"
          and for "both" (where it still applies to the load delta), with an
          inline hint clarifying the scope on "both". */}
      {target !== "reps" && (
        <Col xs={12} sm={6}>
          <FieldGroup label="Direzione">
            <Select
              className="w-100"
              value={direction ?? undefined}
              onChange={onDirectionChange}
              placeholder="Scegli..."
              options={[
                { label: "Incremento ↑", value: "increment" },
                { label: "Decremento ↓", value: "decrement" },
              ]}
            />
            {target === "both" && (
              <div style={{ fontSize: 11, color: "var(--app-gray-500)", marginTop: 2 }}>
                (la direzione si applica solo al carico)
              </div>
            )}
          </FieldGroup>
        </Col>
      )}
      <Col xs={12} sm={6}>
        <FieldGroup label="Cosa modificare">
          <Select
            className="w-100"
            value={target ?? undefined}
            onChange={(val) => onTargetChange(val as "load" | "reps" | "both")}
            placeholder="Scegli..."
            options={[
              { label: "Carico", value: "load" },
              { label: "Reps", value: "reps" },
              { label: "Entrambi", value: "both" },
            ]}
          />
        </FieldGroup>
      </Col>
      {(target === "load" || target === "both") && (
        <Col xs={12} sm={6}>
          <FieldGroup label="Valore incremento">
            <DecimalInputNumber
              className="w-100"
              value={loadChangeValue ?? undefined}
              onChange={(v) => onLoadChangeValueChange(v ?? null)}
              min={0}
              step={0.01}
              precision={2}
              placeholder="es. 5"
              addonBefore={
                <Select
                  value={effectiveUnit}
                  onChange={(v) => onLoadChangeTypeChange(v as "percentage" | "kg")}
                  options={[
                    { label: "%", value: "percentage" },
                    { label: "kg", value: "kg" },
                  ]}
                  style={{ width: 58 }}
                  popupMatchSelectWidth={false}
                />
              }
            />
          </FieldGroup>
        </Col>
      )}
      {previewLoad != null && (
        <Col md={6} className="d-flex align-items-end">
          <div className="pp-increment-preview">
            <span>{baseLoad} kg</span>
            <span className="pp-increment-preview-arrow">
              {direction === "decrement" ? "↓" : "↑"}
            </span>
            <strong>{previewLoad} kg</strong>
          </div>
        </Col>
      )}
      {(target === "reps" || target === "both") && (
        <Col xs={12} sm={6}>
          {/* FE-F (#a2bde480) — REPLACE semantics: this is the absolute new
              reps target (e.g. "10" or "8-10"), not a delta. */}
          <FieldGroup label="Reps target">
            <RepsValueInput
              value={repsChange}
              onChange={onRepsChange}
            />
          </FieldGroup>
        </Col>
      )}
    </Row>
  );
}

function WnRmFields({
  wn, onWnChange,
  pct, onPctChange,
  maxReps,
}: {
  wn: number | null | undefined;
  onWnChange: (v: number | null) => void;
  pct: number | null | undefined;
  onPctChange: (v: number | null) => void;
  maxReps?: number;
}) {
  const wnRmLabel =
    wn != null && maxReps != null ? `W${wn}-${maxReps}Rm` : null;

  return (
    <Row className="g-2">
      <Col xs={12} sm={6}>
        <FieldGroup label="Carico W">
          <DecimalInputNumber
            className="w-100"
            value={wn ?? undefined}
            onChange={(v) => onWnChange(v ?? null)}
            min={0}
            addonAfter="kg"
            placeholder="es. 100"
          />
        </FieldGroup>
      </Col>
      <Col xs={12} sm={6}>
        <FieldGroup label="% nRm">
          <DecimalInputNumber
            className="w-100"
            value={pct ?? undefined}
            onChange={(v) => onPctChange(v ?? null)}
            min={1}
            max={100}
            addonAfter="%"
            placeholder="es. 80"
          />
        </FieldGroup>
      </Col>
      {maxReps != null && (
        <Col md={12}>
          <div className="pp-wnrm-info">
            n = {maxReps} reps (estremo sup. repScheme)
            {wnRmLabel && <strong> → {wnRmLabel}</strong>}
          </div>
        </Col>
      )}
    </Row>
  );
}

function ThresholdsMatrixField({
  matrixSource, onMatrixSourceChange,
}: {
  matrixSource: "sts" | "sis" | null | undefined;
  onMatrixSourceChange: (v: "sts" | "sis") => void;
}) {
  return (
    <Row className="g-2">
      <Col md={12}>
        <FieldGroup label="Matrice carico base">
          <Select
            className="w-100"
            value={matrixSource ?? undefined}
            onChange={onMatrixSourceChange}
            placeholder="Scegli la sorgente del carico base..."
            options={[
              { label: "Settimana precedente (Set-to-Set)", value: "sts" },
              { label: "Set precedente nella settimana (Set-in-Set)", value: "sis" },
            ]}
          />
        </FieldGroup>
      </Col>
      <Col md={12}>
        <div className="pp-matrix-info">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>Le soglie sono quelle definite nella settimana 1 e si applicano in base alle reps effettive</span>
        </div>
      </Col>
    </Row>
  );
}

// ─── ProgressionRuleEditor ────────────────────────────────────────────────────

export default function ProgressionRuleEditor({
  rule,
  isFirstWeek,
  setIndex,
  onChange,
  weekRepScheme,
  prevWeekHypLoad,
  prevWeekSetRule,
  prevSetHypLoad,
  prevSetRule,
  prevWeekSet,
  prevSetInWeek,
  weeks,
  wi,
  si,
  gridHypLoad,
  gridHypReps,
  copiedBoxConfig,
  onCopyBoxConfig,
}: {
  rule: PrecompiledWeekProgressionRule;
  isFirstWeek: boolean;
  setIndex: number;
  onChange: (partial: Partial<PrecompiledWeekProgressionRule>) => void;
  weekRepScheme?: string | null;
  prevWeekHypLoad?: number | null;
  prevWeekSetRule?: PrecompiledWeekProgressionRule | null;
  prevSetHypLoad?: number | null;
  prevSetRule?: PrecompiledWeekProgressionRule | null;
  // FE-C (#546b25bd) — full prev-set objects enable threshold-aware
  // inheritance via persisted workReps/workLoad in the inline preview.
  prevWeekSet?: PrecompiledSet | null;
  prevSetInWeek?: PrecompiledSet | null;
  // PROG-ENGINE-V3/4 (#0fffbe95) — full weeks array + this slot's coordinates
  // enable the multi-source picker (enumerateSourceCandidates needs to walk
  // ancestors). Optional so non-grid invocations (unified-set generator) keep
  // working without surfacing the picker.
  weeks?: PrecompiledWeek[];
  wi?: number;
  si?: number;
  // #250d50ec — destination-set parity: when this editor is mounted inside a
  // grid (PrecompiledProgressionForm / ExerciseProgressionEditor /
  // ProgrammazioneDetailEditor), the parent already resolves this slot's
  // hypLoad/hypReps via the walk-back-aware grid. Forward them so the LIVE
  // "Ipotetico (da sorgente)" badge in Section-A displays the same value the
  // source set's tab shows on the chain — even when the local
  // computePrevSlotProjection preview is silent (e.g. source has only a
  // baseline and no forward rule body, so applyRuleStS/SiS returns null but
  // the grid still resolves the next slot via the chain).
  gridHypLoad?: number | null;
  gridHypReps?: number | string | null;
  // Per-box Copia/Incolla. When `onCopyBoxConfig` is provided, each StS / SiS
  // method panel renders a clipboard toolbar. `copiedBoxConfig` is the shared
  // clipboard slot (canonical, suffix-free) — pasting maps it onto the
  // target box's StS- or SiS-suffixed fields via apply*Config. Omitting both
  // (the default) keeps the editor clipboard-free, so provider-less callers
  // and existing unit tests are unaffected.
  copiedBoxConfig?: ProgressionMethodConfig | null;
  onCopyBoxConfig?: (config: ProgressionMethodConfig, kind: "sts" | "sis") => void;
}) {
  const maxReps = parseMaxReps(weekRepScheme ?? undefined);
  const isFirstSet = setIndex === 0;
  const src = rule.loadComputeSource ?? null;
  const pt = rule.progressionType;
  // Wave 1.1 (#99c43a08) — exclusive rendering reverts F10's parity widening.
  // Show only the StS block for "set-to-set", only the SiS block for "set-in-set",
  // both for "both", and neither for "none" / null.
  const showStS = pt === "set-to-set" || pt === "both";
  const showSiS = pt === "set-in-set" || pt === "both";

  // FE-C (#546b25bd) — use the shared computePrevSlotProjection so the inline
  // preview also fires for thresholds-mode prev rules (was silent before).
  const stsPreview = computePrevSlotProjection({
    target: "sts",
    prevWeekSet,
    prevWeekHypLoad,
    prevWeekSetRule,
    weekRepScheme,
  });
  const sisPreview = computePrevSlotProjection({
    target: "sis",
    prevSetInWeek,
    prevSetHypLoad,
    prevSetRule,
    weekRepScheme,
  });

  // FE-H (#130311b7) — stsPreview/sisPreview are now {load, reps} | null.
  // computedLoad/computedReps read the .load/.reps fields per source priority.
  const stsLoad = stsPreview?.load ?? null;
  const sisLoad = sisPreview?.load ?? null;
  const stsReps = stsPreview?.reps ?? null;
  const sisReps = sisPreview?.reps ?? null;
  const computedLoad = (() => {
    if (isFirstWeek && isFirstSet) {
      if ((rule.initialLoadType ?? "fixed") === "fixed") return rule.initialLoad ?? null;
      if (rule.initialLoadType === "dynamic" && rule.initialWnRm != null && rule.initialPnRm != null)
        return Math.round(rule.initialWnRm * rule.initialPnRm / 100 * 100) / 100;
      return null;
    }
    switch (src) {
      case "sts": return stsLoad;
      case "sis": return sisLoad;
      case "both":
        return rule.calculationPriority === "sis" ? (sisLoad ?? stsLoad) : (stsLoad ?? sisLoad);
      case "fixed": return rule.initialLoad ?? null;
      case "dynamic":
        return rule.initialWnRm != null && rule.initialPnRm != null
          ? Math.round(rule.initialWnRm * rule.initialPnRm / 100 * 100) / 100 : null;
      default: return null;
    }
  })();
  const computedReps: number | string | null = (() => {
    if (isFirstWeek && isFirstSet) return null;
    switch (src) {
      case "sts": return stsReps;
      case "sis": return sisReps;
      case "both":
        return rule.calculationPriority === "sis" ? (sisReps ?? stsReps) : (stsReps ?? sisReps);
      default: return null;
    }
  })();

  // PROG-ENGINE-V2/3 (Bug 3) — derive loadComputeSource from progressionType
  // so in-memory state matches what the BE normalizer rehydrates on save.
  // Without this seed, the page-level `computeHypLoad` / `computeHypReps`
  // switches dispatch via `loadComputeSource`, which stays null until
  // save+reopen. `effectiveLoadComputeSource` is the read-side fallback; this
  // seed is the write-side defense so the two stay aligned.
  const derivedLcs = (pt: typeof rule.progressionType): typeof rule.loadComputeSource => {
    switch (pt) {
      case "set-to-set": return "sts";
      case "set-in-set": return "sis";
      case "both": return "both";
      default: return null;
    }
  };

  // #b6c242ff — decouple Section-B's "Confronto set" from Section-A's
  // "Sorgente carico". `progressionType` (Section B — describes the forward
  // rule body shape) and `loadComputeSource` (Section A — describes where
  // this slot's initial load is derived) are conceptually orthogonal, and a
  // user who has already chosen a "Sorgente carico" in Section A must not
  // see it silently overwritten when they pick a different "Confronto set"
  // in Section B. Mirror the seed-once pattern from `switchTypeStS` /
  // `switchTypeSiS` (line 593-612): only seed `loadComputeSource` from
  // `derivedLcs(newPt)` when it is still null — i.e. preserve an explicit
  // user pick, but keep the legacy PROG-ENGINE-V2/3 seed for unconfigured
  // slots so projections fire pre-save.
  const resetPt = (newPt: typeof pt) => onChange({
    progressionType: newPt,
    ...(rule.loadComputeSource == null
      ? { loadComputeSource: derivedLcs(newPt) }
      : {}),
    weekPriority: null, incrementDirection: null, incrementTarget: null,
    repsChange: null, loadChangeType: null, loadChangeValue: null,
    weekWnRm: null, weekPnRm: null, matrixSource: null,
    weekPrioritySiS: null, incrementDirectionSiS: null, incrementTargetSiS: null,
    repsChangeSiS: null, loadChangeTypeSiS: null, loadChangeValueSiS: null,
    weekWnRmSiS: null, weekPnRmSiS: null, matrixSourceSiS: null,
    calculationPriority: null,
  });

  // Wave 2.2 (#02fc6485) — per-tab type switch. Resets only the tab-specific
  // downstream state so the user can mix StS/SiS rule types freely.
  // PROG-ENGINE-V2/3 — when loadComputeSource is still null at this point the
  // user has reached the per-tab type controls without touching Section-A's
  // Sorgente Select; seed from progressionType so projections fire pre-save.
  // Already-set source values are preserved so "fixed" / "dynamic" choices
  // made elsewhere are not clobbered.
  const switchTypeStS = (newType: "fixed" | "thresholds") => onChange({
    type: newType,
    ...(rule.loadComputeSource == null
      ? { loadComputeSource: derivedLcs(rule.progressionType) }
      : {}),
    weekPriority: null, incrementDirection: null,
    repsChange: null, loadChangeType: null, loadChangeValue: null,
    weekWnRm: null, weekPnRm: null, matrixSource: null,
    positiveThresholdsStS: null, negativeThresholdsStS: null,
  });
  const switchTypeSiS = (newType: "fixed" | "thresholds") => onChange({
    typeSiS: newType,
    ...(rule.loadComputeSource == null
      ? { loadComputeSource: derivedLcs(rule.progressionType) }
      : {}),
    weekPrioritySiS: null, incrementDirectionSiS: null,
    repsChangeSiS: null, loadChangeTypeSiS: null, loadChangeValueSiS: null,
    weekWnRmSiS: null, weekPnRmSiS: null, matrixSourceSiS: null,
    positiveThresholdsSiS: null, negativeThresholdsSiS: null,
  });

  const srcOptions = (() => {
    if (isFirstWeek && !isFirstSet) {
      return [
        { label: "Set in Set (set precedente)", value: "sis" },
        { label: "Carico Fisso", value: "fixed" },
        { label: "Calcolo Dinamico (WnRm × %)", value: "dynamic" },
      ];
    }
    if (!isFirstWeek && isFirstSet) {
      return [
        { label: "Set to Set (stessa posizione sett. prec.)", value: "sts" },
        { label: "Carico Fisso", value: "fixed" },
        { label: "Calcolo Dinamico (WnRm × %)", value: "dynamic" },
      ];
    }
    return [
      { label: "Set to Set (stessa posizione sett. prec.)", value: "sts" },
      { label: "Set in Set (set precedente stessa sett.)", value: "sis" },
      { label: "StS + SiS", value: "both" },
      { label: "Carico Fisso", value: "fixed" },
      { label: "Calcolo Dinamico (WnRm × %)", value: "dynamic" },
    ];
  })();

  return (
    <>
      {/* ── SEZIONE A: Carico iniziale ── */}
      {/* FE-N (#84fcb586) — wrapped in a Level-2 CollapsibleSection. Title
          unified to "Carico iniziale" per the spec; the original variant
          ("Calcolo Carico di Questo Set" for non-first slots) was dropped per
          user request. Default OPEN. */}
      <CollapsibleSection
        title="Carico iniziale"
        icon={faDumbbell}
        level={2}
        defaultOpen
      >
      <div className="pp-section-a">

        {isFirstWeek && isFirstSet && (
          <Row className="g-2">
            <Col xs={12} sm={6}>
              <FieldGroup label="Tipologia carico iniziale">
                <Select
                  className="w-100"
                  value={rule.initialLoadType ?? "fixed"}
                  onChange={(val) => onChange({
                    initialLoadType: val as "fixed" | "dynamic",
                    initialLoad: null, initialWnRm: null, initialPnRm: null,
                  })}
                  options={[
                    { label: "Carico Fisso", value: "fixed" },
                    { label: "Calcolo Dinamico (WnRm × %)", value: "dynamic" },
                  ]}
                />
              </FieldGroup>
            </Col>

            {(rule.initialLoadType ?? "fixed") === "fixed" && (
              <Col xs={12} sm={6}>
                <FieldGroup label="Carico iniziale">
                  <DecimalInputNumber
                    className="w-100"
                    value={rule.initialLoad ?? undefined}
                    onChange={(v) => onChange({ initialLoad: v ?? null })}
                    min={0}
                    step={0.01}
                    precision={2}
                    addonAfter="kg"
                    placeholder="es. 60"
                  />
                </FieldGroup>
              </Col>
            )}

            {rule.initialLoadType === "dynamic" && (
              <Col md={12}>
                <WnRmFields
                  wn={rule.initialWnRm}
                  onWnChange={(v) => onChange({ initialWnRm: v })}
                  pct={rule.initialPnRm}
                  onPctChange={(v) => onChange({ initialPnRm: v })}
                  maxReps={maxReps}
                />
              </Col>
            )}

            {computedLoad != null && (
              <Col md={12}>
                <div className="pp-source-preview">
                  Carico ipotetico: <strong>{computedLoad} kg</strong>
                </div>
              </Col>
            )}

          </Row>
        )}

        {!(isFirstWeek && isFirstSet) && (
          <Row className="g-2">
            <Col xs={12} sm={6}>
              <FieldGroup label="Sorgente carico">
                <Select
                  className="w-100"
                  value={src ?? undefined}
                  onChange={(val) => onChange({
                    loadComputeSource: val as typeof src,
                    initialLoad: null, initialWnRm: null, initialPnRm: null,
                    calculationPriority: null,
                    loadComputeSourceRef: null,
                    initialLoadType: val === "fixed" ? "fixed" : val === "dynamic" ? "dynamic" : undefined,
                  })}
                  placeholder="Seleziona sorgente..."
                  options={srcOptions}
                />
              </FieldGroup>
            </Col>

            {src === "both" && (
              <Col xs={12} sm={6}>
                <FieldGroup label="Priorità calcolo">
                  <Select
                    className="w-100"
                    value={rule.calculationPriority ?? undefined}
                    onChange={(val) => onChange({ calculationPriority: val as "sts" | "sis" })}
                    placeholder="Quale ha la precedenza?"
                    options={[
                      { label: "Set to Set", value: "sts" },
                      { label: "Set in Set", value: "sis" },
                    ]}
                  />
                </FieldGroup>
              </Col>
            )}

            {/* PROG-ENGINE-V3/4 (#0fffbe95) — multi-source picker. Renders only
                when (a) the editor is mounted inside the grid (weeks/wi/si
                provided), (b) the active source is grid-resolved (sts/sis/both),
                and (c) the relevant axis has >1 eligible ancestor. For "both"
                we render one picker per axis side-by-side. */}
            {weeks && wi != null && si != null && (src === "sts" || src === "both") && (() => {
              const candidates = enumerateSourceCandidates(weeks, wi, si, "sts");
              if (candidates.length <= 1) return null;
              const pin = rule.loadComputeSourceRef;
              const value = pin && pin.axis === "sts" ? pin.sourceOffset : null;
              const defaultCand = candidates.find((c) => c.isDefault);
              const options = [
                {
                  label: defaultCand
                    ? `Default (consigliato): ${defaultCand.label}`
                    : "Default (consigliato)",
                  value: 0,
                  title: defaultCand?.labelFull,
                },
                ...candidates.map((c) => ({
                  label: c.label,
                  value: c.sourceOffset,
                  title: c.labelFull,
                })),
              ];
              return (
                <Col xs={12} sm={src === "both" ? 6 : 12}>
                  <FieldGroup label={src === "both" ? "Sorgente carico StS" : "Sorgente carico (ancestor)"}>
                    <Tooltip
                      title={
                        value && pin
                          ? candidates.find((c) => c.sourceOffset === value)?.labelFull ?? null
                          : defaultCand?.labelFull ?? null
                      }
                    >
                      <Select
                        className="w-100"
                        value={value ?? 0}
                        onChange={(val) => {
                          if (val === 0) onChange({ loadComputeSourceRef: null });
                          else onChange({ loadComputeSourceRef: { axis: "sts", sourceOffset: val as number } });
                        }}
                        options={options}
                        optionRender={(opt) => (
                          <Tooltip title={(opt.data as { title?: string }).title}>
                            <span>{opt.label}</span>
                          </Tooltip>
                        )}
                      />
                    </Tooltip>
                  </FieldGroup>
                </Col>
              );
            })()}

            {weeks && wi != null && si != null && (src === "sis" || src === "both") && (() => {
              const candidates = enumerateSourceCandidates(weeks, wi, si, "sis");
              if (candidates.length <= 1) return null;
              const pin = rule.loadComputeSourceRef;
              const value = pin && pin.axis === "sis" ? pin.sourceOffset : null;
              const defaultCand = candidates.find((c) => c.isDefault);
              const options = [
                {
                  label: defaultCand
                    ? `Default (consigliato): ${defaultCand.label}`
                    : "Default (consigliato)",
                  value: 0,
                  title: defaultCand?.labelFull,
                },
                ...candidates.map((c) => ({
                  label: c.label,
                  value: c.sourceOffset,
                  title: c.labelFull,
                })),
              ];
              return (
                <Col xs={12} sm={src === "both" ? 6 : 12}>
                  <FieldGroup label={src === "both" ? "Sorgente carico SiS" : "Sorgente carico (ancestor)"}>
                    <Tooltip
                      title={
                        value && pin
                          ? candidates.find((c) => c.sourceOffset === value)?.labelFull ?? null
                          : defaultCand?.labelFull ?? null
                      }
                    >
                      <Select
                        className="w-100"
                        value={value ?? 0}
                        onChange={(val) => {
                          if (val === 0) onChange({ loadComputeSourceRef: null });
                          else onChange({ loadComputeSourceRef: { axis: "sis", sourceOffset: val as number } });
                        }}
                        options={options}
                        optionRender={(opt) => (
                          <Tooltip title={(opt.data as { title?: string }).title}>
                            <span>{opt.label}</span>
                          </Tooltip>
                        )}
                      />
                    </Tooltip>
                  </FieldGroup>
                </Col>
              );
            })()}

            {src === "fixed" && (
              <Col xs={12} sm={6}>
                <FieldGroup label="Carico">
                  <DecimalInputNumber
                    className="w-100"
                    value={rule.initialLoad ?? undefined}
                    onChange={(v) => onChange({ initialLoad: v ?? null })}
                    min={0}
                    step={0.01}
                    precision={2}
                    addonAfter="kg"
                    placeholder="es. 65"
                  />
                </FieldGroup>
              </Col>
            )}

            {src === "dynamic" && (
              <Col md={12}>
                <WnRmFields
                  wn={rule.initialWnRm}
                  onWnChange={(v) => onChange({ initialWnRm: v })}
                  pct={rule.initialPnRm}
                  onPctChange={(v) => onChange({ initialPnRm: v })}
                  maxReps={maxReps}
                />
              </Col>
            )}

            {/* FE-H (#130311b7) — render Carico+Reps badges INSIDE the Calcolo
                panel when sorgente is sts/sis/both. Replaces the plain-text
                preview from FE-C with the same `pp-calc-badge-*` form factor
                used by the per-set "Carico ipotetico (template)" parent block.
                #250d50ec — prefer the grid-resolved hypLoad/hypReps for this
                slot when available so the destination set's badge mirrors what
                the source set's tab shows on the chain; fall back to the local
                computePrevSlotProjection result for non-grid mounts. */}
            {(() => {
              const isSrcDriven = src === "sts" || src === "sis" || src === "both";
              if (!isSrcDriven) return null;
              const displayLoad = gridHypLoad != null ? gridHypLoad : computedLoad;
              const displayReps = gridHypReps != null ? gridHypReps : computedReps;
              if (displayLoad == null && displayReps == null) return null;
              return (
                <Col md={12}>
                  <div className="pp-calc-result">
                    <div className="pp-source-context">
                      {src === "sts" && prevWeekHypLoad != null && (
                        <>Sett. prec.: {prevWeekHypLoad} kg</>
                      )}
                      {src === "sis" && prevSetHypLoad != null && (
                        <>Set prec.: {prevSetHypLoad} kg</>
                      )}
                      {src === "both" && (
                        <>
                          {prevWeekHypLoad != null && <>Sett. prec.: {prevWeekHypLoad} kg</>}
                          {prevWeekHypLoad != null && prevSetHypLoad != null && " · "}
                          {prevSetHypLoad != null && <>Set prec.: {prevSetHypLoad} kg</>}
                        </>
                      )}
                    </div>
                    <span className="pp-calc-result-label">
                      Carico ipotetico ({src === "sts"
                        ? "set to set"
                        : src === "sis"
                        ? "set in set"
                        : "set to set + set in set"})
                    </span>
                    <div className="pp-calc-badges">
                      {displayLoad != null && (
                        <span className="pp-calc-badge pp-calc-badge-load">{displayLoad} kg</span>
                      )}
                      {displayReps != null && (
                        <span className="pp-calc-badge pp-calc-badge-reps">{displayReps} reps</span>
                      )}
                    </div>
                  </div>
                </Col>
              );
            })()}
          </Row>
        )}
      </div>
      </CollapsibleSection>

      {/* ── SEZIONE B: Regola di progressione futura ── */}
      {/* FE-N (#84fcb586) — Level-2 CollapsibleSection, default OPEN. */}
      <CollapsibleSection
        title="Regola di progressione futura"
        icon={faArrowTrendUp}
        level={2}
        defaultOpen
      >
      <div className="pp-section-b">

        <Row className="g-2">
          <Col md={12}>
            <FieldGroup label="Confronto set">
              <Select
                className="w-100"
                value={pt ?? undefined}
                onChange={(val) => resetPt(val as typeof pt)}
                placeholder="Seleziona..."
                options={[
                  { label: "Nessuno", value: "none" },
                  { label: "Set to Set (sett. successiva)", value: "set-to-set" },
                  { label: "Set in Set (prossimo set)", value: "set-in-set" },
                  { label: "Entrambi", value: "both" },
                ]}
              />
            </FieldGroup>
          </Col>
        </Row>

        {/* Wave 2.2 (#02fc6485) — StS tab: per-tab type Select + applyForNextWeeks
            + body (fixed or thresholds based on rule.type).
            #b4812cad — "Cosa modificare" moved into each tab's thresholds body. */}
        {showStS && (() => {
          const effType = rule.type ?? "fixed";
          return (
            // FE-N (#84fcb586) — Level-3 collapsible, default OPEN.
            <CollapsibleSection
              title="Set to Set — Settimana successiva"
              level={3}
              defaultOpen
            >
            <div className="pp-method-block">
              {onCopyBoxConfig && (
                <BoxClipboardToolbar
                  onCopy={() => onCopyBoxConfig(extractStSConfig(rule), "sts")}
                  onPaste={
                    copiedBoxConfig
                      ? () => onChange(applyStSConfig(copiedBoxConfig))
                      : undefined
                  }
                />
              )}
              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <FieldGroup label="Tipo (StS)">
                    <Select
                      className="w-100"
                      value={effType}
                      onChange={(val) => switchTypeStS(val as "fixed" | "thresholds")}
                      options={[
                        { label: "Progressione Fissa", value: "fixed" },
                        { label: "Soglie", value: "thresholds" },
                      ]}
                    />
                  </FieldGroup>
                </Col>
                <Col xs={12} sm={6}>
                  <FieldGroup label="Applica per (settimane successive)">
                    <DecimalInputNumber
                      className="w-100"
                      min={1}
                      placeholder="1"
                      value={rule.applyForNextWeeks ?? undefined}
                      onChange={(v) => onChange({ applyForNextWeeks: v ?? null })}
                    />
                  </FieldGroup>
                </Col>
              </Row>
              {effType === "fixed" && (
                <>
                  <Row className="g-2">
                    <Col md={12}>
                      <FieldGroup label="Metodo">
                        <Select
                          className="w-100"
                          value={rule.weekPriority ?? undefined}
                          onChange={(val) => onChange({
                            weekPriority: val as "fixed_increment" | "wn_rm" | "none",
                            incrementDirection: null, incrementTarget: null,
                            repsChange: null, loadChangeType: null, loadChangeValue: null,
                            weekWnRm: null, weekPnRm: null,
                          })}
                          placeholder="Seleziona metodo..."
                          options={[
                            { label: "Nessuna", value: "none" },
                            { label: "Incremento Fisso", value: "fixed_increment" },
                            { label: "WnRm + %nRm", value: "wn_rm" },
                          ]}
                        />
                      </FieldGroup>
                    </Col>
                  </Row>
                  {rule.weekPriority === "fixed_increment" && (
                    <FixedIncrementFields
                      direction={rule.incrementDirection}
                      onDirectionChange={(v) => onChange({ incrementDirection: v })}
                      target={rule.incrementTarget}
                      onTargetChange={(v) => onChange({ incrementTarget: v, repsChange: null, loadChangeType: null, loadChangeValue: null })}
                      repsChange={rule.repsChange}
                      onRepsChange={(v) => onChange({ repsChange: v })}
                      loadChangeType={rule.loadChangeType}
                      onLoadChangeTypeChange={(v) => onChange({ loadChangeType: v })}
                      loadChangeValue={rule.loadChangeValue}
                      // #dcf0ceef — seed loadChangeType to "kg" when the user
                      // types a value without touching the unit Select. The
                      // unit dropdown shows "kg" as visual default (via
                      // `effectiveUnit = loadChangeType ?? "kg"`), but the
                      // persisted field stayed null and starved
                      // `applyRuleStS` (strict-equality check on
                      // loadChangeType) → grid returned null → "Ipotetico"
                      // badge missing on the destination set.
                      onLoadChangeValueChange={(v) =>
                        onChange(
                          v != null && rule.loadChangeType == null
                            ? { loadChangeValue: v, loadChangeType: "kg" }
                            : { loadChangeValue: v },
                        )
                      }
                      baseLoad={computedLoad}
                    />
                  )}
                  {rule.weekPriority === "wn_rm" && (
                    <WnRmFields
                      wn={rule.weekWnRm}
                      onWnChange={(v) => onChange({ weekWnRm: v })}
                      pct={rule.weekPnRm}
                      onPctChange={(v) => onChange({ weekPnRm: v })}
                      maxReps={maxReps}
                    />
                  )}
                </>
              )}
              {effType === "thresholds" && (
                <>
                  {/* #b4812cad — per-tab "Cosa modificare" (StS). */}
                  <Row className="g-2">
                    <Col xs={12} sm={6}>
                      <FieldGroup label="Cosa modificare (soglie)">
                        <Select
                          className="w-100"
                          value={rule.incrementTarget ?? undefined}
                          onChange={(val) => onChange({
                            incrementTarget: val as "load" | "reps" | "both",
                            loadChangeType: null,
                            positiveThresholdsStS: null, negativeThresholdsStS: null,
                          })}
                          placeholder="Scegli..."
                          options={[
                            { label: "Carico", value: "load" },
                            { label: "Reps", value: "reps" },
                            { label: "Entrambi", value: "both" },
                          ]}
                        />
                      </FieldGroup>
                    </Col>
                    {(rule.incrementTarget === "load" || rule.incrementTarget === "both") && (
                      <Col xs={12} sm={6}>
                        <FieldGroup label="Tipo variazione carico">
                          <Select
                            className="w-100"
                            value={rule.loadChangeType ?? undefined}
                            onChange={(val) => onChange({
                              loadChangeType: val as "percentage" | "kg",
                              positiveThresholdsStS: null, negativeThresholdsStS: null,
                            })}
                            placeholder="% o kg fissi"
                            options={[
                              { label: "Percentuale (%)", value: "percentage" },
                              { label: "Kg fissi", value: "kg" },
                            ]}
                          />
                        </FieldGroup>
                      </Col>
                    )}
                  </Row>
                  {/* Threshold rows render once "Cosa modificare" is set, and
                      for the load/both branches once "Tipo variazione carico"
                      is also picked. Reps-only is shown immediately. */}
                  {rule.incrementTarget && (rule.incrementTarget === "reps" || rule.loadChangeType) && (
                    <>
                      {!isFirstWeek && (
                        <ThresholdsMatrixField
                          matrixSource={rule.matrixSource}
                          onMatrixSourceChange={(v) => onChange({ matrixSource: v })}
                        />
                      )}
                      <ThresholdSection
                        title=""
                        positiveThresholds={rule.positiveThresholdsStS ?? []}
                        negativeThresholds={rule.negativeThresholdsStS ?? []}
                        onChangePositive={(v) => onChange({ positiveThresholdsStS: v })}
                        onChangeNegative={(v) => onChange({ negativeThresholdsStS: v })}
                        incrementTarget={rule.incrementTarget}
                        loadChangeType={rule.loadChangeType ?? null}
                      />
                    </>
                  )}
                </>
              )}
            </div>
            </CollapsibleSection>
          );
        })()}

        {showSiS && (() => {
          const effType = rule.typeSiS ?? rule.type ?? "fixed";
          return (
            // FE-N (#84fcb586) — Level-3 collapsible, default OPEN.
            <CollapsibleSection
              title="Set in Set — Prossimo set"
              level={3}
              defaultOpen
            >
            <div className="pp-method-block">
              {onCopyBoxConfig && (
                <BoxClipboardToolbar
                  onCopy={() => onCopyBoxConfig(extractSiSConfig(rule), "sis")}
                  onPaste={
                    copiedBoxConfig
                      ? () => onChange(applySiSConfig(copiedBoxConfig))
                      : undefined
                  }
                />
              )}
              <Row className="g-2">
                <Col xs={12} sm={6}>
                  <FieldGroup label="Tipo (SiS)">
                    <Select
                      className="w-100"
                      value={effType}
                      onChange={(val) => switchTypeSiS(val as "fixed" | "thresholds")}
                      options={[
                        { label: "Progressione Fissa", value: "fixed" },
                        { label: "Soglie", value: "thresholds" },
                      ]}
                    />
                  </FieldGroup>
                </Col>
                <Col xs={12} sm={6}>
                  <FieldGroup label="Applica per (set successivi)">
                    <DecimalInputNumber
                      className="w-100"
                      min={1}
                      placeholder="1"
                      value={rule.applyForNextSets ?? undefined}
                      onChange={(v) => onChange({ applyForNextSets: v ?? null })}
                    />
                  </FieldGroup>
                </Col>
              </Row>
              {effType === "fixed" && (
                <>
                  <Row className="g-2">
                    <Col md={12}>
                      <FieldGroup label="Metodo">
                        <Select
                          className="w-100"
                          value={rule.weekPrioritySiS ?? undefined}
                          onChange={(val) => onChange({
                            weekPrioritySiS: val as "fixed_increment" | "wn_rm" | "none",
                            incrementDirectionSiS: null, incrementTargetSiS: null,
                            repsChangeSiS: null, loadChangeTypeSiS: null, loadChangeValueSiS: null,
                            weekWnRmSiS: null, weekPnRmSiS: null,
                          })}
                          placeholder="Seleziona metodo..."
                          options={[
                            { label: "Nessuna", value: "none" },
                            { label: "Incremento Fisso", value: "fixed_increment" },
                            { label: "WnRm + %nRm", value: "wn_rm" },
                          ]}
                        />
                      </FieldGroup>
                    </Col>
                  </Row>
                  {rule.weekPrioritySiS === "fixed_increment" && (
                    <FixedIncrementFields
                      direction={rule.incrementDirectionSiS}
                      onDirectionChange={(v) => onChange({ incrementDirectionSiS: v })}
                      target={rule.incrementTargetSiS}
                      onTargetChange={(v) => onChange({ incrementTargetSiS: v, repsChangeSiS: null, loadChangeTypeSiS: null, loadChangeValueSiS: null })}
                      repsChange={rule.repsChangeSiS}
                      onRepsChange={(v) => onChange({ repsChangeSiS: v })}
                      loadChangeType={rule.loadChangeTypeSiS}
                      onLoadChangeTypeChange={(v) => onChange({ loadChangeTypeSiS: v })}
                      loadChangeValue={rule.loadChangeValueSiS}
                      // #dcf0ceef — mirror of the StS callsite: seed
                      // loadChangeTypeSiS to "kg" when the user types a value
                      // without touching the SiS unit Select. The visual
                      // default is "kg" via `effectiveUnit = loadChangeType ??
                      // "kg"`; persisting it eliminates the divergence that
                      // made `applyRuleSiS` return null and starved the grid
                      // for the destination slot.
                      onLoadChangeValueChange={(v) =>
                        onChange(
                          v != null && rule.loadChangeTypeSiS == null
                            ? { loadChangeValueSiS: v, loadChangeTypeSiS: "kg" }
                            : { loadChangeValueSiS: v },
                        )
                      }
                      baseLoad={computedLoad}
                    />
                  )}
                  {rule.weekPrioritySiS === "wn_rm" && (
                    <WnRmFields
                      wn={rule.weekWnRmSiS}
                      onWnChange={(v) => onChange({ weekWnRmSiS: v })}
                      pct={rule.weekPnRmSiS}
                      onPctChange={(v) => onChange({ weekPnRmSiS: v })}
                      maxReps={maxReps}
                    />
                  )}
                </>
              )}
              {effType === "thresholds" && (() => {
                // #b4812cad — per-tab "Cosa modificare" for SiS, with back-compat
                // fallback to rule.incrementTarget when typeSiS-specific field is unset.
                const sisTarget = rule.incrementTargetSiS ?? rule.incrementTarget ?? null;
                return (
                  <>
                    <Row className="g-2">
                      <Col xs={12} sm={6}>
                        <FieldGroup label="Cosa modificare (soglie)">
                          <Select
                            className="w-100"
                            value={sisTarget ?? undefined}
                            onChange={(val) => onChange({
                              incrementTargetSiS: val as "load" | "reps" | "both",
                              loadChangeTypeSiS: null,
                              positiveThresholdsSiS: null, negativeThresholdsSiS: null,
                            })}
                            placeholder="Scegli..."
                            options={[
                              { label: "Carico", value: "load" },
                              { label: "Reps", value: "reps" },
                              { label: "Entrambi", value: "both" },
                            ]}
                          />
                        </FieldGroup>
                      </Col>
                      {(sisTarget === "load" || sisTarget === "both") && (
                        <Col xs={12} sm={6}>
                          <FieldGroup label="Tipo variazione carico (SiS)">
                            <Select
                              className="w-100"
                              value={rule.loadChangeTypeSiS ?? undefined}
                              onChange={(val) => onChange({
                                loadChangeTypeSiS: val as "percentage" | "kg",
                                positiveThresholdsSiS: null, negativeThresholdsSiS: null,
                              })}
                              placeholder="% o kg fissi"
                              options={[
                                { label: "Percentuale (%)", value: "percentage" },
                                { label: "Kg fissi", value: "kg" },
                              ]}
                            />
                          </FieldGroup>
                        </Col>
                      )}
                    </Row>
                    {sisTarget && (sisTarget === "reps" || rule.loadChangeTypeSiS) && (
                      <>
                        {!isFirstWeek && (
                          <ThresholdsMatrixField
                            matrixSource={rule.matrixSourceSiS}
                            onMatrixSourceChange={(v) => onChange({ matrixSourceSiS: v })}
                          />
                        )}
                        <ThresholdSection
                          title=""
                          positiveThresholds={rule.positiveThresholdsSiS ?? []}
                          negativeThresholds={rule.negativeThresholdsSiS ?? []}
                          onChangePositive={(v) => onChange({ positiveThresholdsSiS: v })}
                          onChangeNegative={(v) => onChange({ negativeThresholdsSiS: v })}
                          incrementTarget={sisTarget}
                          loadChangeType={rule.loadChangeTypeSiS ?? null}
                        />
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            </CollapsibleSection>
          );
        })()}

      </div>
      </CollapsibleSection>

    </>
  );
}
